import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Star, Minus, Plus, ShoppingCart, Heart, Share2, Truck, Shield, RefreshCcw, ChevronLeft, Trash2, User } from 'lucide-react';
import { productsAPI, reviewsAPI } from '../lib/api';
import { Textarea } from '../components/ui/textarea'; // Assuming you have textarea component or just use <textarea>
// Actually I don't see Textarea imported, I'll use native textarea or check if it exists. 
// StoreLayout used Input from ui/input. I'll stick to basic components to avoid issues if Textarea doesn't exist.
import { getImageUrl } from '../lib/utils';
import { fetchWithCache } from '../lib/cache';
import SEOHead from '../components/SEOHead';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isWholesale, user, isAdmin } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  // Review State
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rating: 0, comment: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [id]);

  useEffect(() => {
    if (!product?.id) return;
    try {
      const key = 'pwa_recently_viewed';
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      const next = [
        {
          id: product.id,
          name: product.name,
          image: product.images?.[0],
          price: product.selling_price,
        },
        ...stored.filter((item) => item.id !== product.id),
      ].slice(0, 8);
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  }, [product]);

  const fetchReviews = async () => {
    try {
      const response = await reviewsAPI.getAll(id);
      setReviews(response.data || []);
    } catch (error) {
      // Failed to fetch reviews
    }
  };

  const handleSubmitReview = async () => {
    if (newReview.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      await reviewsAPI.create({
        product_id: id,
        rating: newReview.rating,
        comment: newReview.comment
      });
      toast.success('Review submitted successfully!');
      setNewReview({ rating: 0, comment: '' });
      fetchReviews(); // Refresh list
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await reviewsAPI.delete(reviewId);
      toast.success('Review deleted');
      fetchReviews();
    } catch (error) { // Syntax error fixed here if present in original thought
      console.error('Failed to delete review:', error);
      toast.error('Failed to delete review');
    }
  };

  const fetchProduct = async () => {
    try {
      await fetchWithCache(
        `product_detail_${id}`,
        async () => {
          const response = await productsAPI.getOne(id);
          return response.data;
        },
        (data, isFromCache) => {
          setProduct(data);

          // Set initial quantity based on user role
          let initialQty = data.minimum_purchase_qty || 1;

          // For wholesale users, enforce wholesale minimum quantity
          if (isWholesale && data.wholesale_price && data.wholesale_min_qty) {
            initialQty = Math.max(initialQty, data.wholesale_min_qty);
          }

          setQuantity(initialQty);
          if (isFromCache) setLoading(false);
        }
      );
    } catch (error) {
      // console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      // If we didn't load from cache (or if fresh fetch finishes), ensure loading is false
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="aspect-square bg-muted rounded-2xl animate-pulse" />
          <div className="space-y-4">
            <div className="h-8 bg-muted rounded animate-pulse" />
            <div className="h-6 bg-muted rounded w-1/2 animate-pulse" />
            <div className="h-12 bg-muted rounded w-1/3 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  // Determine which price to show based on user role
  let basePrice = product.selling_price;
  let showWholesale = false;

  if (user?.role === 'pos_user') {
    // POS users see only POS price
    basePrice = product.pos_price || product.selling_price;
  } else if (isWholesale && product.wholesale_price) {
    // Wholesale users see wholesale price
    basePrice = product.wholesale_price;
    showWholesale = true;
  }
  // Admin and normal customers see selling price (default)

  // Exact stock is only sent to staff; customers get `in_stock`. null = unknown (server checks at order time).
  const knownStock = typeof product.stock_qty === 'number' ? product.stock_qty : null;
  const outOfStock = knownStock !== null ? knownStock <= 0 : product.in_stock === false;
  const lowStock = knownStock !== null && knownStock > 0 && knownStock <= (product.low_stock_threshold ?? 0);

  const qualifiesForWholesale = showWholesale && quantity >= (product.wholesale_min_qty || 10);
  const currentPrice = basePrice;
  const discount = Math.round(((product.mrp - currentPrice) / product.mrp) * 100);

  const handleAddToCart = () => {
    addItem(product, quantity);
    toast.success('Added to cart!');
  };

  const handleBuyNow = () => {
    addItem(product, quantity);
    if (user) {
      navigate('/checkout');
    } else {
      navigate('/login?redirect=/checkout');
    }
  };

  // Hide Buy Now for POS and Wholesale users
  const showBuyNow = user?.role !== 'pos_user' && !isWholesale;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="product-detail-page">
      <SEOHead
        title={product.name}
        description={product.description}
        image={getImageUrl(product.images?.[0])}
      />
      {/* Breadcrumb */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        Back to Products
      </button>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <div className="space-y-4">
          <div className="aspect-square rounded-2xl overflow-hidden bg-muted">
            <img
              src={getImageUrl(product.images?.[selectedImage]) || 'https://images.unsplash.com/photo-1624927637280-f033784c1279?w=800'}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto scrollbar-invisible pb-2">
              {product.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${selectedImage === index ? 'border-primary' : 'border-transparent'
                    }`}
                >
                  <img
                    src={getImageUrl(img)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {discount > 0 && <Badge className="discount-badge">{discount}% OFF</Badge>}
            {showWholesale && <Badge className="wholesale-badge">Wholesale Available</Badge>}
            {lowStock && (
              <Badge variant="outline" className="text-amber-600 border-amber-600">Low Stock</Badge>
            )}
            {outOfStock && (
              <Badge variant="destructive">Out of Stock</Badge>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold">{product.name}</h1>

          <div className="flex items-center gap-2 mt-2">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < 4 ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
              ))}
            </div>
            <span className="text-sm text-muted-foreground">(120 reviews)</span>
          </div>

          {/* Price */}
          <div className="mt-6 p-4 bg-muted/50 rounded-xl">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-extrabold price-tag text-primary">
                ₹{currentPrice.toLocaleString()}
              </span>
              <span className="text-lg text-muted-foreground line-through">
                ₹{product.mrp.toLocaleString()}
              </span>
              {discount > 0 && (
                <span className="text-emerald-600 font-semibold">Save ₹{(product.mrp - currentPrice).toLocaleString()}</span>
              )}
            </div>
            {showWholesale && (
              <div className="mt-2 p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm font-medium text-primary">
                  {qualifiesForWholesale
                    ? '✓ Wholesale price applied!'
                    : `Add ${product.wholesale_min_qty - quantity} more for wholesale price of ₹${product.wholesale_price.toLocaleString()}`
                  }
                </p>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Inclusive of all taxes | GST: {product.gst_rate}%
            </p>
          </div>

          {/* Quantity */}
          <div className="mt-6">
            <label className="text-sm font-medium mb-2 block">Quantity</label>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  // For wholesale users, enforce wholesale minimum
                  const minQty = isWholesale && product.wholesale_price
                    ? Math.max(product.minimum_purchase_qty || 1, product.wholesale_min_qty || 10)
                    : (product.minimum_purchase_qty || 1);
                  setQuantity(Math.max(minQty, quantity - 1));
                }}
                disabled={quantity <= (isWholesale && product.wholesale_price
                  ? Math.max(product.minimum_purchase_qty || 1, product.wholesale_min_qty || 10)
                  : (product.minimum_purchase_qty || 1))}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-12 text-center font-semibold text-lg">{quantity}</span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setQuantity(knownStock !== null ? Math.min(knownStock, quantity + 1) : quantity + 1)}
                disabled={outOfStock || (knownStock !== null && quantity >= knownStock)}
              >
                <Plus className="w-4 h-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {knownStock !== null ? `${knownStock} available` : (outOfStock ? 'Out of stock' : 'In stock')}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Button
              className={showBuyNow ? "flex-1 btn-primary" : "w-full btn-primary"}
              onClick={handleAddToCart}
              disabled={outOfStock}
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Add to Cart
            </Button>
            {showBuyNow && (
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-bold"
                onClick={handleBuyNow}
                disabled={outOfStock}
                data-testid="buy-now-btn"
              >
                Buy Now
              </Button>
            )}
          </div>

          <div className="flex gap-4 mt-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: product.name,
                    text: product.description,
                    url: window.location.href,
                  }).catch((error) => {
                    // Error sharing - fallback to clipboard
                  });
                } else {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copied to clipboard!');
                }
              }}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {/* Features */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-muted/50 rounded-xl">
              <Truck className="w-6 h-6 mx-auto text-primary mb-2" />
              <p className="text-xs font-medium">Free Delivery</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-xl">
              <Shield className="w-6 h-6 mx-auto text-primary mb-2" />
              <p className="text-xs font-medium">Secure Payment</p>
            </div>
            <div className="text-center p-3 bg-muted/50 rounded-xl">
              <RefreshCcw className="w-6 h-6 mx-auto text-primary mb-2" />
              <p className="text-xs font-medium">Easy Returns</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="description" className="mt-12">
        <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0">
          <TabsTrigger value="description" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
            Description
          </TabsTrigger>
          <TabsTrigger value="specifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
            Specifications
          </TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">
            Reviews
          </TabsTrigger>
        </TabsList>
        <TabsContent value="description" className="mt-6">
          <Card className="p-6">
            <p className="text-muted-foreground leading-relaxed">
              {product.description || 'No description available for this product.'}
            </p>
          </Card>
        </TabsContent>
        <TabsContent value="specifications" className="mt-6">
          <Card className="p-6">
            <dl className="space-y-3">
              <div className="flex justify-between py-2 border-b">
                <dt className="text-muted-foreground">SKU</dt>
                <dd className="font-mono">{product.sku}</dd>
              </div>
              {product.hsn_code && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-muted-foreground">HSN Code</dt>
                  <dd className="font-mono">{product.hsn_code}</dd>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <dt className="text-muted-foreground">GST Rate</dt>
                <dd>{product.gst_rate}%</dd>
              </div>
              {product.weight && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-muted-foreground">Weight</dt>
                  <dd>{product.weight} kg</dd>
                </div>
              )}
              {product.color && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-muted-foreground">Color</dt>
                  <dd>{product.color}</dd>
                </div>
              )}
              {product.material && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-muted-foreground">Material</dt>
                  <dd>{product.material}</dd>
                </div>
              )}
              {product.origin && (
                <div className="flex justify-between py-2 border-b">
                  <dt className="text-muted-foreground">Country of Origin</dt>
                  <dd>{product.origin}</dd>
                </div>
              )}
            </dl>
          </Card>
        </TabsContent>
        <TabsContent value="reviews" className="mt-6">
          <Card className="p-6">
            <div className="space-y-8">
              {/* Add Review Section */}
              {user ? (
                <div className="bg-muted/30 p-4 rounded-xl border">
                  <h3 className="font-semibold mb-4">Write a Review</h3>
                  <div className="space-y-4">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setNewReview(prev => ({ ...prev, rating: star }))}
                          className="focus:outline-none transition-transform hover:scale-110"
                        >
                          <Star
                            className={`w-6 h-6 ${star <= newReview.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                          />
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-muted-foreground">{newReview.rating > 0 ? `${newReview.rating} Stars` : 'Select rating'}</span>
                    </div>
                    <textarea
                      value={newReview.comment}
                      onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                      placeholder="Share your thoughts about this product..."
                      className="w-full min-h-[100px] p-3 rounded-lg border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <Button
                      onClick={handleSubmitReview}
                      disabled={submitting || newReview.rating === 0}
                      className="btn-primary"
                    >
                      {submitting ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 bg-muted/30 rounded-xl border border-dashed">
                  <p className="text-muted-foreground mb-4">Please login to write a review</p>
                  <Button variant="outline" onClick={() => navigate('/login')}>Login to Review</Button>
                </div>
              )}

              {/* Reviews List */}
              <div className="space-y-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  Customer Reviews
                  <Badge variant="secondary" className="rounded-full">{reviews.length}</Badge>
                </h3>

                {reviews.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No reviews yet. Be the first to review!</p>
                ) : (
                  reviews.map((review) => (
                    <div key={review.id} className="border-b last:border-0 pb-6 last:pb-0">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                            {(review.user?.name || review.user_name || 'U')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{review.user?.name || review.user_name || 'Anonymous'}</p>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-3 h-3 ${i < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteReview(review.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 mt-2 text-sm leading-relaxed">
                        {review.comment}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(review.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
